/**
 * Flat, purple-led profile building blocks shared by /profile (MyProfile) and /user/:id (UserProfile).
 * Rules: scripts/design/DESIGN_SYSTEM.md (tinted cards, solid stickers, subject colours, no gradients).
 */
import { ReactNode, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Illustration } from "@/components/Illustration";
import { StudentEmptyState } from "@/components/StudentEmptyState";
import { BadgeArt } from "@/components/BadgeArt";
import { ALL_BADGES, RARITY_CONFIG } from "@/lib/badges";
import { subjectStyleFor, STICKER_TILT } from "@/lib/subjectStyles";
import { computeLevel } from "@shared/xp";
import { Trophy, Zap, Target, Flame, ShieldCheck, Calculator, Languages, FlaskConical, Globe, Brain, BookOpen, Users, Star, Medal, Calendar, School } from "lucide-react";

export interface ProfileUser {
  id: string; firstName: string; lastName: string; username?: string | null; grade?: number | string;
  school?: string | null; profilePhotoUrl?: string | null; bio?: string | null; role?: string; createdAt?: string;
}
export interface ConnectionUser { id: string; firstName: string; lastName: string; profilePhotoUrl?: string; grade?: number; school?: string; currentLevel?: number }

const SUBJECT_LABELS: Record<string, string> = { math: "Mathematics", mathematics: "Mathematics", english: "Language Arts", "language arts": "Language Arts", science: "Science", "social studies": "Social Studies", "ability test": "Ability Test" };
const SUBJECT_ICONS: Record<string, any> = { math: Calculator, mathematics: Calculator, english: Languages, "language arts": Languages, science: FlaskConical, "social studies": Globe, "ability test": Brain };
export const subjectLabel = (s: string) => SUBJECT_LABELS[s.toLowerCase().trim()] ?? s.replace(/\b\w/g, c => c.toUpperCase());
const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString();

export function Initials({ user, className = "", onPurple = false }: { user: Pick<ProfileUser, "firstName" | "lastName" | "profilePhotoUrl">; className?: string; onPurple?: boolean }) {
  return (
    <Avatar className={className}>
      <AvatarImage src={user.profilePhotoUrl || ""} alt={`${user.firstName} ${user.lastName}`} className="object-cover" />
      <AvatarFallback className={`font-bold ${onPurple ? "bg-white text-primary" : "bg-primary/15 text-primary"}`}>{user.firstName?.[0]}{user.lastName?.[0]}</AvatarFallback>
    </Avatar>
  );
}

/** Purple hero band: avatar, name, chips, bio, counts, actions; rank + points stickers on the right. */
export function ProfileHero({ user, rank, totalPoints, level, followerCount, followingCount, pendingCount = 0, avatar, bio, actions, onCounts, joinedLabel }: {
  user: ProfileUser; rank: number | null; totalPoints: number; level: number; followerCount: number; followingCount: number; pendingCount?: number;
  avatar: ReactNode; bio?: ReactNode; actions?: ReactNode; onCounts?: () => void; joinedLabel?: string | null;
}) {
  const username = user.username ? `@${user.username}` : null;
  return (
    <section className="rounded-2xl bg-primary text-primary-foreground p-5 md:p-7 relative overflow-hidden" data-testid="profile-hero">
      <div className="flex flex-col md:flex-row gap-5 md:gap-7 md:items-start">
        <div className="shrink-0 self-center md:self-start">{avatar}</div>
        <div className="flex-1 min-w-0 text-center md:text-left">
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight leading-tight" data-testid="text-profile-name">{user.firstName} {user.lastName}</h1>
            {user.role === "admin" && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/20">Staff</span>}
          </div>
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-1.5 mt-2">
            {username && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/20">{username}</span>}
            {user.grade != null && user.grade !== "" && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/20">Grade {user.grade}</span>}
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/20"><Star className="w-3 h-3" />Level {level}</span>
            {joinedLabel && <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-white/10 text-primary-foreground/90"><Calendar className="w-3 h-3" />Joined {joinedLabel}</span>}
          </div>
          {user.school && <p className="mt-2 text-sm text-primary-foreground/85 flex items-center gap-1.5 justify-center md:justify-start"><School className="w-4 h-4 shrink-0" /><span className="truncate">{user.school}</span></p>}
          {bio !== undefined ? <div className="mt-2">{bio}</div> : user.bio ? <p className="mt-2 text-sm text-primary-foreground/90 leading-snug max-w-xl">{user.bio}</p> : null}
          <button type="button" onClick={onCounts} className="mt-2 text-sm flex items-center gap-1.5 hover:underline mx-auto md:mx-0" data-testid="button-profile-counts">
            <Users className="w-4 h-4" />
            <span><span className="font-bold">{fmt(followingCount)}</span> Following</span>
            <span className="opacity-60">·</span>
            <span><span className="font-bold">{fmt(followerCount)}</span> Followers</span>
            {pendingCount > 0 && <span className="ml-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950">{pendingCount} new</span>}
          </button>
          {actions && <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-4">{actions}</div>}
        </div>
        <div className="flex md:flex-col gap-3 justify-center shrink-0">
          <div className="rounded-xl bg-white/15 px-4 py-3 min-w-[7.5rem] text-center" data-testid="tile-rank">
            <div className="text-2xl md:text-3xl font-bold tabular-nums leading-none">{rank ? `#${rank}` : "–"}</div>
            <div className="text-xs font-medium text-primary-foreground/80 mt-1">{user.grade ? `Rank in Grade ${user.grade}` : "Rank"}</div>
          </div>
          <div className="rounded-xl bg-white/15 px-4 py-3 min-w-[7.5rem] text-center" data-testid="tile-points">
            <div className="text-2xl md:text-3xl font-bold tabular-nums leading-none">{fmt(totalPoints)}</div>
            <div className="text-xs font-medium text-primary-foreground/80 mt-1">Points</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Four flat stat tiles: accuracy, questions, streak, badges. */
export function ProfileStatTiles({ accuracy, totalQuestions, totalCorrect, streak, badges, xp }: { accuracy: number; totalQuestions: number; totalCorrect: number; streak: number; badges: number; xp: number }) {
  const tiles = [
    { icon: Target, label: "Accuracy", value: `${Math.round(accuracy || 0)}%`, sub: `${fmt(totalCorrect)} of ${fmt(totalQuestions)} right`, tint: "bg-blue-500/10", sticker: "bg-blue-500", id: "accuracy" },
    { icon: Zap, label: "XP earned", value: fmt(xp), sub: "from questions and lessons", tint: "bg-amber-500/10", sticker: "bg-amber-500", id: "xp" },
    { icon: Flame, label: "Day streak", value: fmt(streak), sub: streak > 0 ? "days in a row" : "start one today", tint: "bg-orange-500/10", sticker: "bg-orange-500", id: "streak" },
    { icon: ShieldCheck, label: "Badges", value: fmt(badges), sub: badges > 0 ? "earned so far" : "none yet", tint: "bg-violet-500/10", sticker: "bg-violet-500", id: "badges" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="profile-stats">
      {tiles.map(t => (
        <div key={t.id} className={`rounded-xl border border-border ${t.tint} p-3 md:p-4 flex items-center gap-3`} data-testid={`stat-${t.id}`}>
          <div className={`w-11 h-11 rounded-xl ${t.sticker} text-white flex items-center justify-center shrink-0 -rotate-2 shadow-sm`}><t.icon className="w-5 h-5" /></div>
          <div className="min-w-0">
            <div className="text-xl md:text-2xl font-bold tabular-nums leading-tight">{t.value}</div>
            <div className="text-xs font-semibold leading-tight">{t.label}</div>
            <div className="text-[11px] text-muted-foreground leading-tight">{t.sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Subject cards in the subject's own colour: level chip, points, share of all points. */
export function SubjectMastery({ subjects, ownerFirstName }: { subjects: Array<{ subject: string; points: number }>; ownerFirstName?: string }) {
  const total = subjects.reduce((a, s) => a + (s.points || 0), 0);
  const sorted = [...subjects].sort((a, b) => b.points - a.points);
  return (
    <section className="rounded-xl border border-border bg-card p-4 md:p-5 space-y-3" data-testid="profile-subjects">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base md:text-lg font-semibold inline-flex items-center gap-2"><BookOpen className="w-5 h-5 text-primary" />Subjects</h2>
        <span className="text-xs font-medium text-muted-foreground tabular-nums">{fmt(total)} points across {sorted.length} {sorted.length === 1 ? "subject" : "subjects"}</span>
      </div>
      {sorted.length === 0 ? (
        <StudentEmptyState illustration="owl-book" title={ownerFirstName ? `${ownerFirstName} has not started a subject yet.` : "No subjects yet."} hint="Points show up here after the first quiz." size={100} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {sorted.map((s, i) => {
            const st = subjectStyleFor(s.subject);
            const Icon = SUBJECT_ICONS[s.subject.toLowerCase().trim()] ?? BookOpen;
            const lvl = computeLevel(s.points);
            const share = total ? Math.round((s.points / total) * 100) : 0;
            const toNext = Math.max(0, lvl.nextLevelXP - lvl.levelXP);
            const levelPct = lvl.nextLevelXP > 0 ? Math.min(100, Math.round((lvl.levelXP / lvl.nextLevelXP) * 100)) : 0;
            return (
              <div key={s.subject} className={`rounded-xl border ${st.card} p-4 space-y-3`} data-testid={`subject-${s.subject}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl ${st.sticker} flex items-center justify-center shrink-0 ${STICKER_TILT[i % STICKER_TILT.length]} shadow-sm`}><Icon className="w-6 h-6" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold leading-tight truncate">{subjectLabel(s.subject)}</div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${st.chip}`}>Level {lvl.currentLevel}</span>
                      <span className="text-xs font-medium text-muted-foreground tabular-nums">{fmt(s.points)} pts · {share}%</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className={`h-2 rounded-full ${st.track} overflow-hidden`} role="progressbar" aria-valuenow={levelPct} aria-valuemin={0} aria-valuemax={100}>
                    <div className={`h-full rounded-full ${st.bar}`} style={{ width: `${levelPct}%` }} />
                  </div>
                  <div className={`text-xs ${st.text} tabular-nums`}>{fmt(toNext)} XP to level {lvl.currentLevel + 1}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** Earned badges with rarity tint; Owlbert with a padlock when there are none. */
export function BadgesSection({ achievements, isSelf, ownerFirstName }: { achievements: Array<{ type: string; earnedAt: string }>; isSelf: boolean; ownerFirstName?: string }) {
  const earnedMap = new Map(achievements.map(a => [a.type, a.earnedAt]));
  const earned = ALL_BADGES.filter(b => earnedMap.has(b.type)).sort((a, b) => new Date(earnedMap.get(b.type)!).getTime() - new Date(earnedMap.get(a.type)!).getTime());
  const tint: Record<string, string> = { common: "bg-slate-500/10 border-slate-500/25", rare: "bg-blue-500/10 border-blue-500/25", epic: "bg-violet-500/10 border-violet-500/25", legendary: "bg-amber-500/10 border-amber-500/30" };
  return (
    <section className="rounded-xl border border-border bg-card p-4 md:p-5 space-y-3" data-testid="profile-badges">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base md:text-lg font-semibold inline-flex items-center gap-2"><Medal className="w-5 h-5 text-primary" />Badges</h2>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary tabular-nums">{earned.length} of {ALL_BADGES.length}</span>
      </div>
      {earned.length === 0 ? (
        <StudentEmptyState illustration="owl-lock" title={isSelf ? "No badges yet. Your first one is close." : `${ownerFirstName || "This student"} has no badges yet.`} hint={isSelf ? "Finish a quiz with a perfect score or keep a streak going." : undefined} size={100} />
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {earned.map(b => {
            const rarity = RARITY_CONFIG[b.rarity];
            const at = earnedMap.get(b.type);
            return (
              <div key={b.type} title={`${b.name}: ${b.description}`} className={`rounded-xl border ${tint[b.rarity] ?? tint.common} p-3 flex flex-col items-center text-center gap-1.5`} data-testid={`badge-${b.type}`}>
                <BadgeArt type={b.type} name={b.name} size={56} />
                <p className="text-xs font-semibold leading-tight line-clamp-2">{b.name}</p>
                <p className={`text-[10px] font-semibold uppercase tracking-wide ${rarity.textColor}`}>{rarity.label}</p>
                {at && <p className="text-[10px] text-muted-foreground">{new Date(at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</p>}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** Following / Followers with pill tabs. */
export function ConnectionsCard({ id, following, followers, followerIds, pendingCount = 0, defaultTab, onOpen, onFindPeople, onReviewPending, isSelf }: {
  id?: string; following: ConnectionUser[]; followers: ConnectionUser[]; followerIds?: Set<string>; pendingCount?: number; defaultTab?: "following" | "followers";
  onOpen: (u: ConnectionUser) => void; onFindPeople?: () => void; onReviewPending?: () => void; isSelf: boolean;
}) {
  const [tab, setTab] = useState<"following" | "followers">(defaultTab ?? "following");
  const list = tab === "following" ? following : followers;
  const Pill = ({ value, label, count, alert }: { value: "following" | "followers"; label: string; count: number; alert?: number }) => (
    <button type="button" onClick={() => setTab(value)} className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${tab === value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-primary/10"}`} data-testid={`tab-${value}`}>
      {label}<span className={`text-xs px-1.5 py-0.5 rounded-full tabular-nums ${tab === value ? "bg-white/20" : "bg-primary/10 text-primary"}`}>{count}</span>
      {alert ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950">{alert} new</span> : null}
    </button>
  );
  return (
    <section id={id} className="rounded-xl border border-border bg-card p-4 md:p-5 space-y-3" data-testid="profile-connections">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base md:text-lg font-semibold inline-flex items-center gap-2"><Users className="w-5 h-5 text-primary" />Friends</h2>
        {onFindPeople && <Button size="sm" variant="outline" className="rounded-lg" onClick={onFindPeople} data-testid="button-find-people">Find people</Button>}
      </div>
      <div className="flex gap-1 p-1 rounded-full bg-muted">
        <Pill value="following" label="Following" count={following.length} />
        <Pill value="followers" label="Followers" count={followers.length} alert={pendingCount} />
      </div>
      {tab === "followers" && pendingCount > 0 && onReviewPending && (
        <div className="rounded-lg bg-amber-500/10 border border-amber-400/60 px-3 py-2 text-sm flex items-center justify-between gap-2">
          <span>{pendingCount} follow {pendingCount === 1 ? "request" : "requests"} waiting</span>
          <Button size="sm" variant="ghost" className="h-7 text-amber-900 dark:text-amber-200" onClick={onReviewPending}>Review</Button>
        </div>
      )}
      {list.length === 0 ? (
        <StudentEmptyState illustration="owl-search" size={90}
          title={tab === "following" ? (isSelf ? "You are not following anyone yet." : "Not following anyone yet.") : (isSelf ? "No followers yet." : "No followers yet.")}
          hint={isSelf && tab === "following" ? "Follow classmates to see what they are up to." : undefined}
          action={isSelf && onFindPeople ? <Button size="sm" onClick={onFindPeople}>Find people</Button> : undefined} />
      ) : (
        <ul className="max-h-80 overflow-y-auto divide-y divide-border -mx-1">
          {list.map(u => (
            <li key={u.id}>
              <button type="button" onClick={() => onOpen(u)} className="w-full flex items-center gap-3 px-1 py-2.5 text-left rounded-lg hover:bg-primary/5 transition-colors" data-testid={`connection-${u.id}`}>
                <Initials user={u} className="h-10 w-10" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{u.firstName} {u.lastName}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.school || (u.grade ? `Grade ${u.grade}` : "")}</p>
                </div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary shrink-0">Lv {u.currentLevel ?? 1}</span>
                {followerIds?.has(u.id) && tab === "following" && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-800 dark:text-emerald-200 shrink-0">Follows you</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Small "top 3" ribbon under the hero when the student is on the podium. */
export function PodiumRibbon({ rank, grade }: { rank: number | null; grade?: number | string }) {
  if (!rank || rank > 3) return null;
  const word = rank === 1 ? "first" : rank === 2 ? "second" : "third";
  return (
    <div className="rounded-xl border border-amber-400/60 bg-amber-500/10 px-4 py-3 flex items-center gap-3" data-testid="profile-podium">
      <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0 rotate-2 shadow-sm"><Trophy className="w-5 h-5" /></div>
      <p className="text-sm font-medium">On the podium: {word} place in {grade ? `Grade ${grade}` : "the grade"} right now.</p>
      <Illustration name="owl-trophy" size={44} className="ml-auto hidden sm:block" />
    </div>
  );
}
